import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog, DialogContent, Step, StepLabel, Stepper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import {
  BulkVerificationRunError,
  BulkVerificationRunScope,
  loadVerificationCredits,
  previewBulkVerificationRun,
  startBulkVerificationRun,
  StartedBulkVerificationRun,
  VerificationCredits,
} from 'api/bitedb/entities/bulkVerificationRun.service';
import BulkVerificationScopeStep, {
  BulkVerificationScopeChoice,
} from './BulkVerificationScopeStep';
import BulkVerificationReviewStep, {
  BulkVerificationPreviewState,
  BulkVerificationStartFailure,
  isGateRefusal,
} from './BulkVerificationReviewStep';
import BulkVerificationStartedStep from './BulkVerificationStartedStep';
import BulkVerificationPicker from './BulkVerificationPicker';
import { MAX_SELECTED_ENTITIES } from './bulkVerificationCandidates';
import { formatCount, plural } from './bulkVerificationFormat';

export interface BulkVerificationWizardDatabase {
  id: string;
  name: string;
}

interface BulkVerificationWizardProps {
  database: BulkVerificationWizardDatabase;
  teamId?: string;
  entityCount?: number;
  filtersActive: boolean;
  /** Database Entity ids to start from; the wizard opens on them when any. */
  initialEntityIds?: string[];
  onClose: () => void;
  onRunStarted?: (run: StartedBulkVerificationRun) => void;
  /** A 409 at start: the database already has a run in progress. */
  onRunInProgress?: () => void;
}

type WizardStep = 'scope' | 'review' | 'started';

const creditsFrom = (
  details: unknown,
): { required: number; remaining: number } | null => {
  const { required, remaining } = (details ?? {}) as Record<string, unknown>;
  return typeof required === 'number' && typeof remaining === 'number'
    ? { required, remaining }
    : null;
};

const STEPS: Array<{ id: WizardStep; title: string }> = [
  { id: 'scope', title: 'Scope' },
  { id: 'review', title: 'Review & confirm' },
  { id: 'started', title: 'Started' },
];

const stepperSx = {
  '& .MuiStepIcon-root': { color: 'var(--bg-secondary)' },
  '& .MuiStepIcon-text': { fill: 'var(--text-secondary)' },
  '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
    color: 'var(--action-color)',
  },
  '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': { fill: '#fff' },
  '& .MuiStepLabel-label': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text-tertiary)',
  },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
    color: 'var(--text-primary-color)',
    fontWeight: 600,
  },
  '& .MuiStepConnector-line': { borderColor: 'var(--es-soft-border)' },
  '& .Mui-completed .MuiStepConnector-line, & .Mui-active .MuiStepConnector-line':
    { borderColor: 'var(--action-color)' },
};

/**
 * The Bulk Verification Run wizard, in the app's modal shell: Scope, then
 * Review & confirm (a no-reservation preview of the cost), then Started.
 * Mount it only while open, so each opening starts from Scope.
 */
const BulkVerificationWizard: React.FC<BulkVerificationWizardProps> = ({
  database,
  teamId,
  entityCount,
  filtersActive,
  initialEntityIds,
  onClose,
  onRunStarted,
  onRunInProgress,
}) => {
  const history = useHistory();
  const [step, setStep] = useState<WizardStep>('scope');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(initialEntityIds ?? []),
  );
  const [scope, setScope] = useState<BulkVerificationScopeChoice>(() =>
    initialEntityIds?.length ? 'selected' : 'all',
  );
  const [previewState, setPreviewState] =
    useState<BulkVerificationPreviewState>({ status: 'loading' });
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [credits, setCredits] = useState<VerificationCredits | null>(null);
  const [creditsAttempt, setCreditsAttempt] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [startFailure, setStartFailure] =
    useState<BulkVerificationStartFailure | null>(null);
  const [startedRun, setStartedRun] =
    useState<StartedBulkVerificationRun | null>(null);
  const [removedCount, setRemovedCount] = useState(0);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const runScope = useMemo<BulkVerificationRunScope>(
    () =>
      scope === 'all'
        ? { databaseId: database.id }
        : {
            databaseId: database.id,
            databaseEntityIds: Array.from(selectedIds),
          },
    [database.id, scope, selectedIds],
  );
  const scopeCount = scope === 'all' ? entityCount : selectedIds.size;
  // The picker fills the modal's height, so its pages fit without scrolling.
  const fillsHeight = step === 'scope' && scope === 'selected';
  const selectionBlocked =
    scope === 'selected' &&
    (selectedIds.size === 0 || selectedIds.size > MAX_SELECTED_ENTITIES);

  /** Drops ids the service no longer knows; true when the selection changed. */
  const dropUnknownIds = useCallback(
    (error: BulkVerificationRunError): boolean => {
      if (scope !== 'selected' || error.refusal !== 'unknown-ids') return false;
      const unknown = new Set<string>(
        Array.isArray(error.errors) ? error.errors.map(String) : [],
      );
      const remaining = new Set(
        Array.from(selectedIds).filter((id) => !unknown.has(id)),
      );
      const removed = selectedIds.size - remaining.size;
      if (removed === 0) return false;
      setSelectedIds(remaining);
      setRemovedCount((count) => count + removed);
      if (remaining.size === 0) setStep('scope');
      return true;
    },
    [scope, selectedIds],
  );

  useEffect(() => {
    if (step !== 'review' || runScope.databaseEntityIds?.length === 0) {
      return undefined;
    }
    let cancelled = false;
    setPreviewState({ status: 'loading' });
    previewBulkVerificationRun(runScope).then((outcome) => {
      if (cancelled) return;
      if (!outcome.ok && dropUnknownIds(outcome.error)) return;
      setPreviewState(
        outcome.ok
          ? { status: 'ready', preview: outcome.value }
          : { status: 'error', error: outcome.error },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [step, runScope, previewAttempt, dropUnknownIds]);

  useEffect(() => {
    if (step !== 'review' || !teamId) return undefined;
    let cancelled = false;
    loadVerificationCredits(teamId).then((loaded) => {
      if (!cancelled) setCredits(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [step, teamId, creditsAttempt]);

  const preview =
    previewState.status === 'ready' ? previewState.preview : undefined;
  const canStart =
    !!preview &&
    !isStarting &&
    !(
      startFailure?.kind === 'refused' &&
      isGateRefusal(startFailure.error.refusal)
    ) &&
    preview.toVerify > 0 &&
    preview.creditsRequired <= preview.creditsRemaining;

  const goToScope = () => {
    setStartFailure(null);
    setRemovedCount(0);
    setStep('scope');
  };

  const goToReview = () => {
    setRemovedCount(0);
    setStep('review');
  };

  const handleStart = async () => {
    if (!canStart) return;
    setIsStarting(true);
    setStartFailure(null);
    const outcome = await startBulkVerificationRun(runScope);
    if (!isMounted.current) return;
    setIsStarting(false);
    if (outcome.ok) {
      setStartedRun(outcome.value);
      setStep('started');
      onRunStarted?.(outcome.value);
      return;
    }
    const { error } = outcome;
    const latest = creditsFrom(error.details);
    if (error.refusal === 'no-credits' && latest) {
      setPreviewState((current) =>
        current.status === 'ready'
          ? {
              status: 'ready',
              preview: {
                ...current.preview,
                creditsRequired: latest.required,
                creditsRemaining: latest.remaining,
              },
            }
          : current,
      );
      setStartFailure({ kind: 'credits-changed' });
      setCreditsAttempt((attempt) => attempt + 1);
      return;
    }
    if (error.refusal === 'in-progress' && onRunInProgress) {
      onRunInProgress();
      return;
    }
    if (dropUnknownIds(error)) return;
    setStartFailure({ kind: 'refused', error: error });
  };

  const startLabel = (() => {
    if (isStarting) return 'Starting…';
    if (!preview) return 'Start verification';
    return `Start verification · ${formatCount(
      preview.creditsRequired,
    )} ${plural(preview.creditsRequired, 'credit', 'credits')}`;
  })();

  return (
    <Dialog
      open
      onClose={(_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      className='dialog_box'
      fullWidth
      maxWidth='lg'
      aria-labelledby='bdb-bulk-verify-title'
      PaperProps={{
        className: `bdb-bulk-verify-paper${fillsHeight ? ' is-fill' : ''}`,
      }}
    >
      <div className='bdb-bulk-verify-head'>
        <div className='audit-add-modal-head-cont-icon'>
          <VerifiedUserOutlinedIcon
            className='bdb-bulk-verify-head-icon'
            aria-hidden='true'
          />
        </div>
        <div className='audit-add-modal-head-cont-supporting'>
          <h1 id='bdb-bulk-verify-title'>Run Business Verification</h1>
          <p>{database.name}</p>
        </div>
        <button
          type='button'
          className='bdb-bulk-verify-close'
          aria-label='Close'
          onClick={onClose}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            aria-hidden='true'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>
      </div>
      <Stepper
        activeStep={STEPS.findIndex((s) => s.id === step)}
        className='bdb-bulk-verify-stepper'
        sx={stepperSx}
      >
        {STEPS.map((s) => (
          <Step key={s.id} completed={step === 'started' || undefined}>
            <StepLabel>{s.title}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <DialogContent className='bdb-bulk-verify-content'>
        <div className='bdb-bulk-verify-body'>
          {removedCount > 0 && step !== 'started' && (
            <div className='bdb-bulk-verify-notice' role='status'>
              <InfoOutlinedIcon
                className='bdb-bulk-verify-notice-icon'
                aria-hidden='true'
              />
              <span>
                {removedCount === 1
                  ? '1 selected entity is no longer in this database, so it was removed from the selection.'
                  : `${formatCount(
                      removedCount,
                    )} selected entities are no longer in this database, so they were removed from the selection.`}
              </span>
            </div>
          )}
          {step === 'scope' && (
            <BulkVerificationScopeStep
              scope={scope}
              onScopeChange={setScope}
              databaseName={database.name}
              selectedCount={selectedIds.size}
              entityCount={entityCount}
              filtersActive={filtersActive}
              picker={
                <BulkVerificationPicker
                  databaseId={database.id}
                  selectedIds={selectedIds}
                  onSelectedIdsChange={setSelectedIds}
                />
              }
            />
          )}
          {step === 'review' && (
            <BulkVerificationReviewStep
              databaseName={database.name}
              scope={scope}
              scopeCount={scopeCount}
              previewState={previewState}
              credits={credits}
              startFailure={startFailure}
              onRetryPreview={() => setPreviewAttempt((n) => n + 1)}
              onRetryStart={handleStart}
              onChangeSelection={goToScope}
              onViewCreditPlans={() =>
                history.push({
                  pathname: '/profile',
                  state: { linkTab: 'Subscriptions' },
                })
              }
            />
          )}
          {step === 'started' && startedRun && (
            <BulkVerificationStartedStep run={startedRun} />
          )}
        </div>
      </DialogContent>
      <div className='bdb-bulk-verify-footer'>
        {step === 'scope' && (
          <>
            <span className='bdb-bulk-verify-footer-note'>
              {scope === 'selected' && selectedIds.size === 0
                ? 'Select at least one entity to continue.'
                : 'Nothing is charged until you confirm on the next step.'}
            </span>
            <button type='button' className='btn-outline' onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn-primary'
              disabled={selectionBlocked}
              onClick={goToReview}
            >
              Review cost
            </button>
          </>
        )}
        {step === 'review' && (
          <>
            <button
              type='button'
              className='btn-outline bdb-bulk-verify-footer-back'
              onClick={goToScope}
              disabled={isStarting}
            >
              Back
            </button>
            {preview && preview.toVerify === 0 && (
              <span className='bdb-bulk-verify-footer-note'>
                Nothing in this selection can be verified.
              </span>
            )}
            <button
              type='button'
              className='btn-primary'
              onClick={handleStart}
              disabled={!canStart}
            >
              {startLabel}
            </button>
          </>
        )}
        {step === 'started' && (
          <button type='button' className='btn-primary' onClick={onClose}>
            Track on the database page
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default BulkVerificationWizard;
